/**
 * CR500 Phase 6A: Deal Card V2 Telemetry
 * 
 * Lightweight event tracking for V2 rollout validation.
 * Tracks renderer usage, presentation mode, and key interactions.
 */

// ================================================================
// TYPES
// ================================================================

export type TelemetryEvent =
  | "deal_card_render"
  | "deal_card_details_open"
  | "deal_card_claim_click"
  | "deal_card_terms_click"
  | "deal_card_view_click"
  | "deal_card_flip_back";

export interface TelemetryPayload {
  event: TelemetryEvent;
  renderer: "v1" | "v2";
  presentationMode?: "flip" | "modal" | "sidePanel" | "expand" | "inlineExpand";
  dealType?: string;
  dealId?: string;
  surface?: "primary" | "details";
  timestamp: number;
  sessionId?: string;
  rolloutReason?: string;
}

// ================================================================
// IN-MEMORY BUFFER (for batching)
// ================================================================

const eventBuffer: TelemetryPayload[] = [];
const MAX_BUFFER_SIZE = 50;
const FLUSH_INTERVAL_MS = 30000; // 30 seconds

let flushTimeout: ReturnType<typeof setTimeout> | null = null;

// ================================================================
// EVENT TRACKING
// ================================================================

/**
 * Track a V2-related event.
 * Events are buffered and logged in batches for debugging.
 */
export function trackDealCardEvent(
  event: TelemetryEvent,
  payload: Omit<TelemetryPayload, "event" | "timestamp">
): void {
  const fullPayload: TelemetryPayload = {
    ...payload,
    event,
    timestamp: Date.now(),
  };
  
  // Add to buffer
  eventBuffer.push(fullPayload);
  
  // Console log for dev visibility
  if (process.env.NODE_ENV === "development") {
    console.debug("[V2Telemetry]", event, {
      renderer: payload.renderer,
      presentationMode: payload.presentationMode,
      dealType: payload.dealType,
    });
  }
  
  // Flush if buffer is full
  if (eventBuffer.length >= MAX_BUFFER_SIZE) {
    flushEvents();
  }
  
  // Schedule flush
  scheduleFlush();
}

/**
 * Track a render event with rollout context.
 */
export function trackRenderEvent(
  renderer: "v1" | "v2",
  options: {
    presentationMode?: string;
    dealType?: string;
    dealId?: string;
    surface?: "primary" | "details";
    rolloutReason?: string;
  } = {}
): void {
  trackDealCardEvent("deal_card_render", {
    renderer,
    presentationMode: options.presentationMode as TelemetryPayload["presentationMode"],
    dealType: options.dealType,
    dealId: options.dealId,
    surface: options.surface,
    rolloutReason: options.rolloutReason,
  });
}

/**
 * Track details interaction events.
 */
export function trackDetailsEvent(
  action: "open" | "flip_back",
  renderer: "v1" | "v2",
  dealType?: string
): void {
  const event: TelemetryEvent = action === "open" 
    ? "deal_card_details_open" 
    : "deal_card_flip_back";
    
  trackDealCardEvent(event, { renderer, dealType });
}

/**
 * Track CTA click events.
 */
export function trackCtaEvent(
  cta: "claim" | "terms" | "view",
  renderer: "v1" | "v2",
  dealType?: string
): void {
  const eventMap: Record<string, TelemetryEvent> = {
    claim: "deal_card_claim_click",
    terms: "deal_card_terms_click",
    view: "deal_card_view_click",
  };
  
  trackDealCardEvent(eventMap[cta], { renderer, dealType });
}

// ================================================================
// FLUSH LOGIC
// ================================================================

function scheduleFlush(): void {
  if (flushTimeout) return;
  
  flushTimeout = setTimeout(() => {
    flushEvents();
    flushTimeout = null;
  }, FLUSH_INTERVAL_MS);
}

/**
 * Flush buffered events.
 * For now, just logs to console. Can be extended to send to analytics endpoint.
 */
export function flushEvents(): void {
  if (eventBuffer.length === 0) return;
  
  // Group by event type for summary
  const summary: Record<string, { v1: number; v2: number }> = {};
  
  for (const event of eventBuffer) {
    if (!summary[event.event]) {
      summary[event.event] = { v1: 0, v2: 0 };
    }
    summary[event.event][event.renderer]++;
  }
  
  console.log("[V2Telemetry] Flush summary:", summary, `(${eventBuffer.length} events)`);
  
  // Clear buffer
  eventBuffer.length = 0;
}

/**
 * Get current buffer contents (for debugging).
 */
export function getEventBuffer(): readonly TelemetryPayload[] {
  return eventBuffer;
}

/**
 * Get telemetry summary (for admin UI).
 */
export function getTelemetrySummary(): {
  totalEvents: number;
  v1Renders: number;
  v2Renders: number;
  detailsOpens: number;
  claimClicks: number;
} {
  const renders = eventBuffer.filter(e => e.event === "deal_card_render");
  const detailsOpens = eventBuffer.filter(e => e.event === "deal_card_details_open");
  const claimClicks = eventBuffer.filter(e => e.event === "deal_card_claim_click");
  
  return {
    totalEvents: eventBuffer.length,
    v1Renders: renders.filter(e => e.renderer === "v1").length,
    v2Renders: renders.filter(e => e.renderer === "v2").length,
    detailsOpens: detailsOpens.length,
    claimClicks: claimClicks.length,
  };
}
